import {
  FORGE_MAX_BOX_IMAGES,
  FORGE_MAX_CREATOR_CUT_PERCENT,
  type ForgeSettings,
  resolveStorefrontFeatures,
  TENANT_ASSET_FILE_ACCEPT,
} from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { toast } from "@loot-os/ui/components/sonner";
import { Switch } from "@loot-os/ui/components/switch";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useState } from "react";

import { SectionSwitch, StorefrontSection } from "../components/StorefrontSection";
import { useStorefrontConfig, useStorefrontConfigQuery } from "../lib/storefront";
import { uploadTenantAssetFile, uploadTenantAssetFiles } from "../lib/tenantAssets";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";

/**
 * The Forge on one storefront environment: the switch (`features.forge`, on the header, saved on
 * flip through `storefront.update` as its one key), then the forge settings, which
 * `storefront.setForgeSettings` replaces whole: whether a creator earns a commission, the largest
 * cut one may pick, and the preset images a forge box chooses from. A commission change reprices
 * every forge box in the environment on the next processing run.
 */
export function StorefrontForgePage() {
  const { can } = useTenantRole();
  const { query, update } = useStorefrontConfig();

  // Narrowing only: the layout renders a section once the same query has data.
  if (!query.data) return null;

  const { config, environment, assetsAvailable } = query.data;
  const enabled = resolveStorefrontFeatures(config.features).forge;
  const canEdit = can("editor");

  return (
    <StorefrontSection
      title="Forge"
      description="Shoppers build boxes of their own from the catalog and publish them for other shoppers to open. Off hides the forge pages and refuses creating and editing forge boxes; a forge box already published still opens, and creators can still claim what they earned."
      environment={environment}
      aside={
        <SectionSwitch
          id="forge-enabled"
          label="Forge"
          checked={enabled}
          disabled={update.isPending || !canEdit}
          onCheckedChange={(checked) => update.mutate({ features: { forge: checked } })}
        />
      }
    >
      <CommissionCard settings={config.forgeSettings} canEdit={canEdit} />
      <BoxImagesCard
        settings={config.forgeSettings}
        canEdit={canEdit}
        uploadsAvailable={assetsAvailable}
      />
    </StorefrontSection>
  );
}

/** `setForgeSettings`, with the cache refreshed and the repricing named in the toast. */
function useSaveForgeSettings(onSaved?: () => void) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { queryOptions } = useStorefrontConfigQuery();
  const mutation = useMutation(
    trpc.storefront.setForgeSettings.mutationOptions({
      onSuccess: ({ settings, repricedBoxCount }) => {
        queryClient.setQueryData(queryOptions.queryKey, (old) =>
          old ? { ...old, config: { ...old.config, forgeSettings: settings } } : old,
        );
        void queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
        onSaved?.();
        toast.success(
          repricedBoxCount === 0
            ? "Forge settings saved."
            : `Forge settings saved. ${repricedBoxCount} forge ${repricedBoxCount === 1 ? "box is" : "boxes are"} repriced within five minutes.`,
        );
      },
    }),
  );
  /**
   * Saves `change` applied to the settings as they stand when it is sent (each save writes the
   * whole record), so one card's save never puts back what another card saved while it waited:
   * an image batch that ends after a commission save keeps the new commission.
   */
  const saveWith = (change: (latest: ForgeSettings) => ForgeSettings, fallback: ForgeSettings) => {
    const latest =
      queryClient.getQueryData(queryOptions.queryKey)?.config.forgeSettings ?? fallback;
    return mutation.mutateAsync({ settings: change(latest) });
  };
  return { ...mutation, saveWith };
}

/** A percent the settings accept: 0 to the ceiling, two decimals at most. */
function parseCutPercent(text: string): number | null {
  // The text, not the float: 0.07 * 100 is 7.000000000000001.
  const trimmed = text.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return value <= FORGE_MAX_CREATOR_CUT_PERCENT ? value : null;
}

/**
 * Whether creators earn a commission, and the largest cut one may pick. A draft until saved; the
 * save sends the settings whole, with the images as they are.
 */
function CommissionCard({ settings, canEdit }: { settings: ForgeSettings; canEdit: boolean }) {
  const [commissionEnabled, setCommissionEnabled] = useState<boolean | null>(null);
  const [maxCut, setMaxCut] = useState<string | null>(null);
  const save = useSaveForgeSettings(() => {
    setCommissionEnabled(null);
    setMaxCut(null);
  });

  const currentEnabled = commissionEnabled ?? settings.commissionEnabled;
  const currentMaxCut = maxCut ?? String(settings.maxCreatorCutPercent);
  const parsedMaxCut = parseCutPercent(currentMaxCut);
  const dirty =
    currentEnabled !== settings.commissionEnabled || parsedMaxCut !== settings.maxCreatorCutPercent;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (parsedMaxCut === null) return;
    void save
      .saveWith(
        (latest) => ({
          ...latest,
          commissionEnabled: currentEnabled,
          maxCreatorCutPercent: parsedMaxCut,
        }),
        settings,
      )
      .catch(() => {});
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Creator commission</CardTitle>
        <CardDescription>
          A creator picks a cut of up to the maximum when they build a box. The cut is added to the
          box's price and paid to the creator on every purchase, and they claim it into their
          balance. A change here reprices every forge box in this environment within five minutes;
          purchases made before it still pay the cut they included.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid gap-5" onSubmit={submit}>
          <div className="flex items-center gap-3">
            <Switch
              id="forge-commission"
              checked={currentEnabled}
              disabled={!canEdit || save.isPending}
              onCheckedChange={setCommissionEnabled}
            />
            <Label htmlFor="forge-commission">Pay creators a commission</Label>
          </div>
          <div className="grid max-w-xs gap-2">
            <Label htmlFor="forge-max-cut">Maximum creator cut (%)</Label>
            <Input
              id="forge-max-cut"
              type="number"
              inputMode="decimal"
              min={0}
              max={FORGE_MAX_CREATOR_CUT_PERCENT}
              step={0.01}
              value={currentMaxCut}
              disabled={!canEdit || save.isPending || !currentEnabled}
              onChange={(e) => setMaxCut(e.target.value)}
              aria-invalid={parsedMaxCut === null}
            />
            <p
              className={`text-xs ${parsedMaxCut === null ? "text-destructive" : "text-muted-foreground"}`}
            >
              {`0 to ${FORGE_MAX_CREATOR_CUT_PERCENT}, two decimals at most. A box built with a larger cut is lowered to it.`}
            </p>
          </div>
          {canEdit && (
            <div>
              <Button type="submit" disabled={!dirty || parsedMaxCut === null || save.isPending}>
                Save
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
}

/**
 * The preset images a creator picks from. Each add or remove saves straight away. With none, a
 * forge box shows the storefront's box placeholder, so an empty list never blocks building.
 */
function BoxImagesCard({
  settings,
  canEdit,
  uploadsAvailable,
}: {
  settings: ForgeSettings;
  canEdit: boolean;
  uploadsAvailable: boolean;
}) {
  const trpc = useTRPC();
  // The upload under way, counted ("2 of 5"), while files are going up.
  const [uploading, setUploading] = useState<{ done: number; of: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const save = useSaveForgeSettings();
  // A failed file is named in the card's own summary, once, so these raise no toast of their own.
  const startUpload = useMutation({
    ...trpc.storefront.startForgeImageUpload.mutationOptions(),
    meta: { showsError: () => true },
  });
  const finishUpload = useMutation({
    ...trpc.storefront.finishForgeImageUpload.mutationOptions(),
    meta: { showsError: () => true },
  });

  const images = settings.boxImages;
  const busy = uploading !== null || save.isPending;

  const saveImages = (change: (boxImages: string[]) => string[]) =>
    save.saveWith((latest) => ({ ...latest, boxImages: change(latest.boxImages) }), settings);

  // Every file picked goes up in turn, then the list saves once with every one that made it; a
  // file that fails is named, and the rest still save. Picks past the limit are left out.
  const pick = async (picked: readonly File[]) => {
    setError(null);
    const room = FORGE_MAX_BOX_IMAGES - images.length;
    const files = picked.slice(0, Math.max(0, room));
    if (files.length === 0) {
      if (picked.length > 0) setError(`at most ${FORGE_MAX_BOX_IMAGES} images`);
      return;
    }
    try {
      const { uploaded, failed } = await uploadTenantAssetFiles(
        files,
        async (file) =>
          (
            await uploadTenantAssetFile(
              file,
              (input) => startUpload.mutateAsync(input),
              (uploadKey) => finishUpload.mutateAsync({ uploadKey }),
            )
          ).publicUrl,
        (done, of) => setUploading({ done, of }),
      );
      // The cap again against the list as it stands at the save: another tab or card may have
      // added images while these went up, and a list over the cap would refuse the whole save.
      let unsaved = 0;
      if (uploaded.length > 0) {
        await saveImages((boxImages) => {
          const kept = uploaded.slice(0, Math.max(0, FORGE_MAX_BOX_IMAGES - boxImages.length));
          unsaved = uploaded.length - kept.length;
          return [...boxImages, ...kept];
        });
      }
      const skipped = picked.length - files.length + unsaved;
      setError(
        [
          ...failed.map(({ name, reason }) => `${name}: ${reason}`),
          ...(skipped > 0 ? [`${skipped} left out: at most ${FORGE_MAX_BOX_IMAGES} images`] : []),
        ].join(" · ") || null,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "the images could not be saved");
    } finally {
      setUploading(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Box images</CardTitle>
        <CardDescription>
          The images a creator chooses from for their box. With none, a forge box shows the
          storefront's box placeholder.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-5">
        {images.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No images yet: every forge box uses the placeholder.
          </p>
        ) : (
          <ul className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
            {images.map((url, index) => (
              <li
                key={url}
                className="group relative aspect-square overflow-hidden rounded-md border bg-muted/40"
              >
                <img src={url} alt="" className="size-full object-contain" />
                {canEdit && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon-xs"
                    className="absolute top-1 right-1"
                    disabled={busy}
                    aria-label={`Remove image ${index + 1}`}
                    onClick={() =>
                      void saveImages((boxImages) =>
                        boxImages.filter((image) => image !== url),
                      ).catch(() => {})
                    }
                  >
                    <X />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        {canEdit &&
          (uploadsAvailable ? (
            <div className="grid max-w-sm gap-2">
              <Label htmlFor="forge-image-upload">Add images</Label>
              <Input
                id="forge-image-upload"
                type="file"
                multiple
                accept={TENANT_ASSET_FILE_ACCEPT}
                disabled={busy}
                className="h-auto py-1.5"
                onChange={(e) => {
                  const files = [...(e.target.files ?? [])];
                  e.target.value = "";
                  void pick(files);
                }}
              />
              {uploading ? (
                <p className="text-xs text-muted-foreground" aria-live="polite">
                  Uploading {uploading.done + 1} of {uploading.of}…
                </p>
              ) : error ? (
                <p className="text-xs text-destructive">{error}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  PNG, JPEG, WEBP or AVIF, 5 MB at most each. Pick several at once; they save as
                  soon as they upload.
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Image uploads are not available on this stage.
            </p>
          ))}
      </CardContent>
    </Card>
  );
}
